import { useMemo } from 'react';
import { useCompanySettings } from './useCompanySettings';
import type { HolidaySyncStatus } from '@/types/companySettings';

export interface PublicHolidayOverlay {
  date: string;       // YYYY-MM-DD
  name: string;       // Holiday name or "Public Holiday"
  source: 'api' | 'custom';
}

/**
 * Derives visible public holidays from company settings for a given date range.
 * Respects excluded synced holidays. No backend call — pure client-side derivation.
 */
export function usePublicHolidayOverlay(startDate: string, endDate: string) {
  const { settings, isLoading } = useCompanySettings();

  const holidays = useMemo((): PublicHolidayOverlay[] => {
    const rawDates: string[] = (settings?.holidays as string[]) || [];
    if (rawDates.length === 0) return [];

    const syncStatus = settings?.holiday_sync_status as HolidaySyncStatus | undefined;
    const metadata = syncStatus?.metadata || [];
    const excludedDates = new Set(syncStatus?.excluded_api_dates || []);

    // Build a name lookup from metadata
    const nameMap = new Map<string, { name?: string; source?: 'api' | 'custom' }>();
    for (const m of metadata) {
      nameMap.set(m.date, m);
    }

    return rawDates
      .filter(d => d >= startDate && d <= endDate)
      .filter(d => {
        // Exclude API holidays that the user explicitly excluded
        const meta = nameMap.get(d);
        if (meta?.source === 'api' && excludedDates.has(d)) return false;
        return true;
      })
      .map(d => {
        const meta = nameMap.get(d);
        return {
          date: d,
          name: meta?.name || 'Public Holiday',
          source: meta?.source || 'custom',
        };
      })
      .sort((a, b) => a.date.localeCompare(b.date));
  }, [settings?.holidays, settings?.holiday_sync_status, startDate, endDate]);

  return { holidays, isLoading };
}

/**
 * Quick lookup: is this YYYY-MM-DD string a public holiday?
 */
export function useIsPublicHoliday(holidays: PublicHolidayOverlay[], dateStr: string): PublicHolidayOverlay | undefined {
  return holidays.find(h => h.date === dateStr);
}
